import {
  KatanaPerpsOrder,
  OrderSide,
  OrderType,
  TimeInForce,
} from '@katanaperps/katana-perps-sdk';

import {
  createLimitOrder,
  createLimitStopOrder,
  createMarketOrder,
} from '#/api/apiExchange';
import { BaseCreateOrderType } from '#/api/apiExchangePlaceOrder';
import { isPositionShort } from '#/api/apiUtils';
import { ApiPosition } from '#/api/types';
import { isTriggerTakeProfit } from '#/components/orders/orderUtil';
import { prepareBothConditionalTPSLOrders } from '#/components/positions/takeProfitStopLoss/tpSlUtils';
import {
  EXCHANGE_DECIMALS,
  MAX_ORDER_AMOUNT_OR_TOTAL,
} from '#/config/constants';
import { trackEvent } from '#/lib/analytics';
import { buildMarketPair } from '#/lib/display/formatting';
import { signTypedDataWithModal } from '#/lib/ethereum/sign/signHashWithModal';
import { TypedDataSignParams } from '#/lib/ethereum/sign/typedData/eip712';
import { roundDownAndFormatWithTrailingZeros } from '#/lib/numbers';
import { DelegatedKeyAuthorization } from '#/reduxState/delegatedKey/authorizationReducer';
import { IWallet } from '#/reduxState/wallet/walletReducer';

import { IAction, PanelState } from './buyPanelReducer';
import { validateTPSLOrThrow } from './tpslUtils';

export const performOrder = async ({
  quantity,
  state,
  dispatch,
  side,
  wallet,
  token1Base,
  token2Quote,
  delegatedWallet,
  preConfirmCheck,
  stepSizeDecimals,
  getBestBidAskPrices,
  indexPrice,
  conditionalTpSl,
  conditionalTpSlMaximumPositionSize,
}: {
  quantity: string | undefined;
  token1Base?: string;
  token2Quote?: string;
  side: OrderSide;
  state: Pick<
    PanelState,
    | 'reduceOnly'
    | 'triggerEnabled'
    | 'triggerPrice'
    | 'triggerType'
    | 'orderType'
    | 'price'
    | 'postOnly'
    | 'limitOrderTimeInForce'
    | 'conditionalTpSl'
  >;
  dispatch: (action: IAction) => void;
  wallet: IWallet;
  delegatedWallet?: DelegatedKeyAuthorization;
  preConfirmCheck?: boolean;
  stepSizeDecimals: number | undefined;
  /** Only needed when trigger is active */
  getBestBidAskPrices: () => { bestBid: string; bestAsk: string } | undefined;
  /** To determine TAKE PROFIT/STOP LOSS for Stop orders - only required for Trigger orders */
  indexPrice: string | undefined;
  conditionalTpSl: PanelState['conditionalTpSl'];
  conditionalTpSlMaximumPositionSize: string | undefined;
}): Promise<KatanaPerpsOrder | false> => {
  dispatch({ type: 'loading', side });

  if (!quantity) {
    throw new Error('Unknown quantity');
  }
  if (!wallet.walletAddress) {
    throw new Error('Locked wallet');
  }

  if (!token1Base || !token2Quote) {
    throw new Error('Missing tokens');
  }

  if (stepSizeDecimals === undefined) {
    throw new Error('Unknown step size');
  }

  // TODO: check if we can place order
  //       based on https://docs.google.com/spreadsheets/d/1af23exVIHmqagqJ4c1sXkQ_8M2nMIcXgZWiF962Lnkc/edit#gid=111368470
  // if (
  //   (state.buy &&
  //     (!quoteAssetAvailableFunds ||
  //       BigNumberJs(quoteAssetAvailableFunds).isEqualTo(0))) ||
  //   (!state.buy &&
  //     (!baseAssetAvailableFunds ||
  //       BigNumberJs(baseAssetAvailableFunds).isEqualTo(0)))
  // ) {
  //   throw new Error('You do not have enough funds to place this order.');
  // }

  if (Number(quantity) > MAX_ORDER_AMOUNT_OR_TOTAL) {
    throw Error('Please enter a valid order and try again.');
  }

  if (state.triggerEnabled && !state.triggerPrice) {
    throw new Error('Error: Stop enabled, but missing trigger price');
  }

  if (preConfirmCheck) {
    dispatch({
      type: 'openConfirm',
      isBuy: side === 'buy',
    });
    return false;
  }

  const baseOrderParams = {
    delegatedKeyWallet: delegatedWallet?.walletAddress || undefined,
    baseSymbol: token1Base,
    quoteSymbol: token2Quote,
    reduceOnly: state.reduceOnly,
    quantity: roundDownAndFormatWithTrailingZeros(
      quantity,
      stepSizeDecimals,
      EXCHANGE_DECIMALS
    ),
    side,
    walletAddress: wallet.walletAddress,
    signTypedData: (...typedData: TypedDataSignParams) =>
      signTypedDataWithModal(
        wallet,
        delegatedWallet,
        typedData,
        'Please sign the order on your device.'
      ),
  } satisfies BaseCreateOrderType;

  const bestPrices = getBestBidAskPrices();
  if (state.triggerEnabled && state.triggerType === 'index' && !indexPrice) {
    throw new Error('Unknown market index price');
  }
  const triggerTakeProfit = state.triggerEnabled
    ? isTriggerTakeProfit({
        side,
        indexPrice,
        triggerType: state.triggerType,
        triggerPrice: Number(state.triggerPrice),
        bestAsk: bestPrices?.bestAsk,
        bestBid: bestPrices?.bestBid,
      })
    : undefined;

  // TODO: allow always ! TODO
  if (!delegatedWallet) {
    throw new Error('Only supported while using Delgated Key');
  }
  if (conditionalTpSl && !conditionalTpSlMaximumPositionSize) {
    throw new Error('Maximum position size unknown');
  }
  if (
    state.conditionalTpSl?.stopLossPrice ||
    state.conditionalTpSl?.takeProfitPrice
  ) {
    validateTPSLOrThrow({
      panelState: {
        ...state,
        size: quantity,
        sizeInQuote: false,
        sizePercentage: 0,
      },
      calculatedInfo: {
        quantityBuy: side === 'buy' ? quantity : undefined,
        quantitySell: side === 'sell' ? quantity : undefined,
      },
    });
  }
  const tpSlParams =
    conditionalTpSl && conditionalTpSlMaximumPositionSize
      ? await prepareBothConditionalTPSLOrders({
          delegatedWallet,
          market: buildMarketPair(token1Base, token2Quote),
          parentOrderSide: side,
          maximumPositionSize: conditionalTpSlMaximumPositionSize,
          stopLossPrice: conditionalTpSl.stopLossPrice,
          stopLossPriceType: conditionalTpSl.stopLossPriceType,
          takeProfitPrice: conditionalTpSl.takeProfitPrice,
          takeProfitPriceType: conditionalTpSl.takeProfitPriceType,
          wallet,
        })
      : undefined;

  let newOrder: KatanaPerpsOrder;

  if (state.orderType === 'market') {
    newOrder = await createMarketOrder(
      state.triggerEnabled
        ? {
            type: triggerTakeProfit
              ? OrderType.takeProfitMarket
              : OrderType.stopLossMarket,

            ...baseOrderParams,
            triggerPrice: Number(state.triggerPrice).toFixed(8),
            triggerType: state.triggerType,
          }
        : { type: OrderType.market, ...baseOrderParams },
      tpSlParams
    );
  } else if (state.orderType === 'limit' && state.triggerEnabled) {
    newOrder = await createLimitStopOrder(
      {
        type: triggerTakeProfit
          ? OrderType.takeProfitLimit
          : OrderType.stopLossLimit,

        ...baseOrderParams,
        price: Number(state.price).toFixed(8),
        triggerPrice: Number(state.triggerPrice).toFixed(8),
        triggerType: state.triggerType,
        timeInForce: state.postOnly
          ? TimeInForce.gtx
          : state.limitOrderTimeInForce,
      },
      tpSlParams
    );
  } else if (state.orderType === OrderType.limit && !state.triggerEnabled) {
    if (state.postOnly && state.limitOrderTimeInForce !== TimeInForce.gtc) {
      // Not possible state - UI should restrict Dropdown when selected POST
      throw new Error(
        'POST-only order can be GTC only. Report this to support.'
      );
    }
    newOrder = await createLimitOrder(
      {
        type: OrderType.limit,
        ...baseOrderParams,
        price: Number(state.price).toFixed(8),
        timeInForce: state.postOnly
          ? TimeInForce.gtx
          : state.limitOrderTimeInForce,
      },
      tpSlParams
    );
  } else {
    throw new Error('Unsupported order type');
  }

  trackEvent('trade_success');
  dispatch({ type: 'success', side });

  // Note that "resetFields" action would be called after short delay
  //  see BuySellPanelWrap component

  return newOrder;
};

/** Perform order to close position - either as market or limit  */
export async function closePosition({
  wallet,
  delegatedWallet,
  position,
  stepSizeDecimals,
  takerOrderMinimum,
  maxQuantity,
  byLimit,
}: {
  wallet: IWallet;
  delegatedWallet: DelegatedKeyAuthorization;
  position: ApiPosition;
  stepSizeDecimals: number;
  takerOrderMinimum: string;
  /** Close only part of position */
  maxQuantity?: string;
  /** Close by Limit order instead of Market (default) */
  byLimit?: {
    price: string;
    postOnly: boolean;
  };
}) {
  const [base, quote] = position.market.split('-');

  const quantity = Math.max(
    Math.abs(Number(maxQuantity || position.quantity)),
    // Allow to place dust order (API accepts this in Reduce-only mode)
    Number(takerOrderMinimum)
  );
  const isShort = isPositionShort(position);

  return performOrder({
    quantity: quantity.toString(),
    conditionalTpSl: undefined,
    conditionalTpSlMaximumPositionSize: undefined,
    state: byLimit
      ? {
          limitOrderTimeInForce: 'gtc',
          orderType: 'limit',
          postOnly: byLimit.postOnly,
          reduceOnly: true,
          triggerEnabled: false,
          triggerType: 'index',
          triggerPrice: undefined,
          price: byLimit.price,
        }
      : {
          limitOrderTimeInForce: 'fok', // ignored by market order
          orderType: 'market',
          postOnly: false,
          reduceOnly: true,
          triggerEnabled: false,
          triggerType: 'index',
          triggerPrice: undefined,
          price: '',
        },
    getBestBidAskPrices: () => undefined,
    indexPrice: undefined,
    dispatch: payload => {
      console.log('Close position debug:', payload);
    },
    side: isShort ? 'buy' : 'sell',
    stepSizeDecimals,
    wallet,
    delegatedWallet,
    preConfirmCheck: false,
    token1Base: base,
    token2Quote: quote,
  });
}
